import React from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { Button, ButtonProps } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/pro-solid-svg-icons";
import { TextVariant, TypographyNew } from "../typography/typography";
import { colors } from "../../utils/colors";
import { getResolvedIcon } from "../../utils/iconResolver";
import { NotificationCardData } from "./notification";
import { AbsenceIcon } from "../../redux/api-model/organization/company";
import { getTranslationParamsByPluralRules } from "../../utils/pluralRules";
import RequestInProgress from "../loading/RequestInProgress";
import useLocalizedDateFormatters from "../common/useLocalizedDateFormatter";
import { DashboardCalendarEventType } from "../../redux/api-model/dashboard-calendar";

const IconWithSpace = styled(FontAwesomeIcon)<{ marginLeft?: boolean }>`
  margin-left: ${(props) => (props.marginLeft ? "9px" : 0)};
  margin-right: 8px;
`;

const AbsenseRange = styled.time`
  display: flex;
  align-items: center;
`;

const StyledRequestInProgress = styled(RequestInProgress)`
  margin-left: 4px;
`;

const StyledActionsContainer = styled.div`
  margin-top: 10px;
`;

const StyledButton = muiStyled(Button)<ButtonProps>(({ color }) => {
  const isPrimary = color === "primary";

  return {
    backgroundColor: isPrimary ? colors.blue1 : colors.gray7,
    color: isPrimary ? colors.white : colors.gray2,
    marginRight: "10px",
    border: "none",
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
      backgroundColor: isPrimary ? null : colors.gray11,
    },
  };
});

type GetDateText = (value: string | Date) => string;

function getDatesInfo(
  getDateText: GetDateText,
  event?: NotificationCardData["event"]
) {
  const isNotValidDate = !event?.startDate;

  if (isNotValidDate) {
    return null;
  }

  const start = getDateText(event.startDate as string);
  const end = event.endDate ? getDateText(event.endDate) : null;

  return {
    start,
    end,
    duration: event.absenceRequestDuration,
  };
}

type AbsenseNotificationBodyProps = {
  event: NotificationCardData["event"];
  body?: string;
};

const QUICK_ACTIONS_AVAILABLE = false;

export default function AbsenseNotificationBody({
  event,
}: AbsenseNotificationBodyProps): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation("notifications");
  const { getDateText } = useLocalizedDateFormatters();
  const datesInfo = getDatesInfo(getDateText, event);
  const iconByType = getResolvedIcon({
    absenceRequestType: event?.absenceType ?? "",
    event: event?.type as DashboardCalendarEventType,
    absenceRequestIcon: event?.absenceRequestIcon as AbsenceIcon,
  });
  const duration = datesInfo?.duration ?? 0;
  const absenceInfoTranslationParams = getTranslationParamsByPluralRules(
    "notification.absenceInfo",
    {
      type: event.absenceType,
      count: duration,
    }
  );

  return (
    <TypographyNew
      variant={TextVariant.body1}
      color={theme.colors.grays2}
      marginTop={"10px"}
    >
      <section>
        {datesInfo ? (
          <>
            <TypographyNew
              variant={TextVariant.subtitle2}
              color={colors.gray1}
              marginBottom="10px"
            >
              <IconWithSpace icon={iconByType} size="xs" />
              {duration && duration > 0
                ? t(...absenceInfoTranslationParams)
                : event.absenceType}
            </TypographyNew>
            <AbsenseRange>
              {datesInfo.start}
              <IconWithSpace marginLeft icon={faArrowRightLong} size="xs" />
              {datesInfo.end ?? <StyledRequestInProgress />}
            </AbsenseRange>
          </>
        ) : null}
        {QUICK_ACTIONS_AVAILABLE ? (
          <StyledActionsContainer>
            <StyledButton variant="contained" color="secondary">
              {t("notification.reject")}
            </StyledButton>
            <StyledButton variant="contained" color="primary">
              {t("notification.approve")}
            </StyledButton>
          </StyledActionsContainer>
        ) : null}
      </section>
    </TypographyNew>
  );
}
